import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  Stack,
  Skeleton,
  Alert,
  LinearProgress,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestorePageOutlinedIcon from "@mui/icons-material/RestorePageOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import { getUsageStats, getLicenseChanges, downloadAddendum } from "../../services/usageStatsService";
import LicensePlansDialog from "./LicensePlansDialog";

const fmtDateOnly = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// Riadok rozpisu (ikona + popis + počet)
const StatRow = ({ Icon, label, value, color = "text.secondary" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.6 }}>
    {Icon && <Icon sx={{ fontSize: 18, color }} />}
    <Typography variant="body2" sx={{ flex: 1, color: "text.secondary" }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mt: 2, mb: 0.5 }}>
    {children}
  </Typography>
);

const DataPanel = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [planOpen, setPlanOpen] = useState(false);
  const [changes, setChanges] = useState([]);
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    getUsageStats()
      .then(setStats)
      .catch((e) => setError(e.message || "Chyba"))
      .finally(() => setLoading(false));
  }, []);

  const loadChanges = useCallback(() => {
    getLicenseChanges().then((d) => setChanges(d.changes || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); loadChanges(); }, [load, loadChanges]);

  const license = stats?.license || {};
  const licValidity = license.valid_until
    ? `do ${fmtDate(license.valid_until)}`
    : (license.is_active ? "Aktívna (bez obmedzenia)" : "Neaktívna");

  return (
    <Box sx={{ px: { xs: 2, md: 2.5 }, pt: { xs: 8, md: 9 }, pb: 3, height: "100%", overflow: "auto" }}>
      <Box sx={{ maxWidth: 380, mx: "auto" }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Dáta</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Prehľad vašej organizácie
      </Typography>

      {loading && (
        <>
          <Skeleton variant="rounded" height={90} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={160} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={120} />
        </>
      )}

      {error && !loading && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

      {stats && !loading && (
        <>
          {/* Celkový počet súborov */}
          <Paper elevation={0} sx={(th) => ({ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: alpha(th.palette.primary.main, 0.05) })}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InsertDriveFileOutlinedIcon color="primary" />
              <Typography variant="body2" color="text.secondary">Celkový počet súborov</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, color: "primary.main", lineHeight: 1.1, mt: 0.5 }}>
              {stats.files.total}
            </Typography>

            {stats.license.price_label && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, flexWrap: "wrap" }}>
                <Chip size="small" color="success" label={stats.license.price_label} sx={{ fontWeight: 700 }} />
                <Typography variant="caption" color="text.secondary">
                  za mesiac · {stats.license.label || stats.license.program || "—"}
                </Typography>
              </Box>
            )}

            {/* Priebežné naplnenie programu (tento mesiac vs mesačný limit) */}
            {stats.license.docs_limit != null && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Naplnenie programu (tento mesiac)</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>{stats.license.fill_pct}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, stats.license.fill_pct || 0)}
                  color={stats.license.fill_pct >= 90 ? "error" : stats.license.fill_pct >= 70 ? "warning" : "primary"}
                  sx={{ height: 8, borderRadius: 5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  {stats.usage.files_this_month} / {stats.license.docs_limit} dok. · program {stats.license.program}
                </Typography>
              </Box>
            )}
            {stats.license.docs_limit == null && stats.license.program && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                Individuálny rozsah programu {stats.license.program} (bez fixného limitu).
              </Typography>
            )}
          </Paper>

          <SectionTitle>Z toho</SectionTitle>
          <StatRow Icon={VisibilityOffOutlinedIcon} color="warning.main" label="Anonymizovaných" value={stats.files.anonymized} />
          <StatRow Icon={LockOutlinedIcon} color="success.main" label="Pseudonymizovaných" value={stats.files.pseudonymized} />
          <StatRow Icon={RestorePageOutlinedIcon} color="info.main" label="De-pseudonymizovaných" value={stats.files.depseudonymized} />
          <StatRow Icon={EditNoteOutlinedIcon} color="secondary.main" label="Anotovaných (ručne)" value={stats.files.annotated} />

          <Divider sx={{ mt: 1.5 }} />
          <SectionTitle>Štatistiky využívania</SectionTitle>
          <StatRow label="Spracované strany" value={stats.usage.pages_total} />
          <StatRow label="Uložené v databáze" value={stats.usage.stored_in_db} />
          <StatRow label="Anonymizačné profily" value={stats.usage.profiles} />
          <StatRow label="Posledná aktivita" value={fmtDate(stats.usage.last_activity)} />

          <Divider sx={{ mt: 1.5 }} />
          <SectionTitle>Licencia</SectionTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <WorkspacePremiumOutlinedIcon sx={{ color: "primary.main" }} />
            <Chip size="small" color="primary" label={`Program ${license.label || license.program || "—"}`} sx={{ fontWeight: 700 }} />
            {license.price_label && <Chip size="small" color="success" label={license.price_label} sx={{ fontWeight: 700 }} />}
            {stats.scope === "all" && <Chip size="small" variant="outlined" label="celá inštalácia" />}
          </Box>
          <StatRow label="Cena za riešenie" value={license.price_label || "—"} />
          {license.access && <StatRow label="Rozsah prístupov" value={license.access} />}
          {license.docs && <StatRow label="Limit dokumentov" value={license.docs} />}
          {license.user_accounts != null && <StatRow label="Užívateľské kontá" value={license.user_accounts} />}
          {license.overage_price && <StatRow label="Cena po prekročení / dok." value={license.overage_price} />}
          <StatRow label="Typ účtu" value={license.account_type === "test" ? "Testovací" : "Produkčný"} />
          <StatRow label="Platnosť licencie" value={licValidity} />
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<SwapHorizIcon />}
            onClick={() => setPlanOpen(true)}
            sx={{ mt: 1.5, borderRadius: 2, fontWeight: 700 }}
          >
            Zmeniť program
          </Button>

          <Divider sx={{ mt: 1.5 }} />
          <SectionTitle>Verzia</SectionTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">Cenzor</Typography>
            <Chip label={`v${stats.version}`} color="default" sx={{ fontWeight: 800, fontFamily: "monospace" }} />
          </Box>
          {stats.version_date && <StatRow label="Dátum verzie" value={fmtDateOnly(stats.version_date)} />}

          <Divider sx={{ mt: 1.5 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 2, mb: 0.5 }}>
            <HistoryOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>
              História zmien licencie
            </Typography>
          </Box>
          {notice && <Alert severity="info" sx={{ mb: 1, borderRadius: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}
          {changes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">Zatiaľ žiadne zmeny licencie.</Typography>
          ) : (
            <Stack spacing={1}>
              {changes.map((c) => (
                <Box key={c.id} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {(c.from_program || "—")} → {(c.to_program || "—")}
                    </Typography>
                    <Chip size="small" label={c.status_label}
                      color={c.status === "approved" ? "success" : c.status === "rejected" ? "error" : "warning"} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    {fmtDate(c.created_at)}{c.to_price ? ` · ${c.to_price}` : ""}
                  </Typography>
                  {c.has_addendum && (
                    <Button size="small" startIcon={<DownloadOutlinedIcon />}
                      onClick={() => downloadAddendum(c.id, c.addendum_no ? `Dodatok_${c.addendum_no}.pdf` : "dodatok.pdf")}
                      sx={{ mt: 0.5, borderRadius: 2 }}>
                      Stiahnuť dodatok č. {c.addendum_no}
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          <LicensePlansDialog
            open={planOpen}
            currentProgram={license.program}
            onClose={() => setPlanOpen(false)}
            onChanged={(res) => {
              if (res && res._status === 201) setNotice(res.detail || "Žiadosť odoslaná, čaká na schválenie.");
              else { setNotice(""); load(); }
              loadChanges();
            }}
          />
        </>
      )}
      </Box>
    </Box>
  );
};

const AboutAppPage = () => {
  const { isLoggedIn, user, isLoading } = useAuth();
  const [iframeLoading, setIframeLoading] = useState(true);

  if (!isLoggedIn || isLoading) {
    return (
      <>
        <Sidebar user={user} />
        <Box sx={{ p: 4 }}><Skeleton variant="rounded" height={400} /></Box>
      </>
    );
  }

  return (
    <>
      <Sidebar user={user} />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { md: "100vh" },
          width: "100%",
        }}
      >
        {/* 1/3 – Dáta */}
        <Box
          sx={{
            width: { xs: "100%", md: "34%" },
            maxWidth: { md: 460 },
            borderRight: { md: "1px solid" },
            borderColor: { md: "divider" },
            height: { xs: "auto", md: "100vh" },
            flexShrink: 0,
          }}
        >
          <DataPanel />
        </Box>

        {/* 2/3 – cenzor.sk */}
        <Box sx={{ flex: 1, position: "relative", minHeight: { xs: 560, md: "100vh" } }}>
          {iframeLoading && <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0 }} />}
          <iframe
            src="https://www.cenzor.sk"
            title="cenzor.sk"
            onLoad={() => setIframeLoading(false)}
            style={{ width: "100%", height: "100%", minHeight: 560, border: "none", display: "block" }}
          />
        </Box>
      </Box>
    </>
  );
};

export default AboutAppPage;
